import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertCircle, User, Plus, Search, X } from 'lucide-react';
import { getTasksForProject, createProjectTask } from '../../lib/supabase/tasks';
import { useEntity } from '../../hooks/useEntity';
import { useAuthStore } from '../../store/authStore';
import { getProjects } from '../../lib/supabase/projects';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Project } from '../../types/project';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high';
  dueDate?: string;
  assignee?: string;
  project?: string;
}

export default function TaskBoard() {
  const location = useLocation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showAddTask, setShowAddTask] = useState(
    location.state?.showAddTask === true
  );
  const navigate = useNavigate();
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    dueDate: '',
    projectId: ''
  });
  const { entity } = useEntity();
  const { user } = useAuthStore();

  useEffect(() => {
    // Check if we were navigated here with showAddTask flag
    if (location.state?.showAddTask) {
      setShowAddTask(true);
      // Clear the state to prevent it from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (entity?.id) {
      loadProjects();
    }
  }, [entity?.id]);

  useEffect(() => {
    if (selectedProject) {
      loadTasks();
    }
  }, [selectedProject, refreshTrigger]);

  const loadProjects = async () => {
    try {
      const data = await getProjects(entity!.id);
      setProjects(data);
      
      // Set the first project as default if available
      if (data.length > 0 && !selectedProject) {
        setSelectedProject(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadTasks = async () => {
    if (!selectedProject || selectedProject === 'all') {
      setTasks([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const projectTasks = await getTasksForProject(selectedProject);
      
      // Convert to the Task interface format
      const formattedTasks = projectTasks.map(pt => ({
        id: pt.id,
        title: pt.title,
        description: pt.description || undefined,
        status: pt.status,
        priority: pt.priority,
        dueDate: pt.due_date,
        assignee: pt.assigned_user?.email?.split('@')[0] || undefined,
        project: projects.find(p => p.id === pt.project_id)?.name
      }));
      
      setTasks(formattedTasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newTask.projectId) {
      setError('Please select a project and ensure you are logged in before adding a task.');
      return;
    }
    
    try {
      await createProjectTask(
        newTask.projectId,
        {
          created_by: user.id,
          created_by: user.id,
          created_by: user.id,
          title: newTask.title,
          description: newTask.description,
          status: newTask.status,
          priority: newTask.priority,
          due_date: newTask.dueDate ? new Date(newTask.dueDate).toISOString() : undefined,
          entity_id: entity?.id // Add entity_id if required by your schema
        }
      );
      
      // Reset form and refresh tasks
      setNewTask({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        dueDate: '',
        projectId: ''
      });
      setShowAddTask(false);
      setError(null);
      
      // Trigger a refresh of the tasks list
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to add task:', error);
      setError('Failed to add task. Please try again.');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-500 bg-red-50';
      case 'medium':
        return 'text-yellow-500 bg-yellow-50';
      case 'low':
        return 'text-green-500 bg-green-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo':
        return 'border-gray-200';
      case 'in_progress':
        return 'border-blue-200';
      case 'review':
        return 'border-purple-200';
      case 'done':
        return 'border-green-200';
      default:
        return 'border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'todo':
        return <Clock className="w-4 h-4 text-gray-400" />;
      case 'in_progress':
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case 'review':
        return <AlertCircle className="w-4 h-4 text-purple-500" />;
      case 'done':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const columns = [
    { id: 'todo', title: 'To Do' },
    { id: 'in_progress', title: 'In Progress' },
    { id: 'review', title: 'Review' },
    { id: 'completed', title: 'Done' }
  ];

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProject(e.target.value);
    if (e.target.value === 'all') {
      setTasks([]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <button 
            onClick={() => setShowAddTask(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Task</span>
          </button>
          <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
            Filter Tasks
          </button>
        </div>
        <div className="flex items-center space-x-4">
          <select 
            className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
            value={selectedProject}
            onChange={handleProjectChange}
          >
            <option value="all">All Projects</option>
            {projects?.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>
      </div>

      {showAddTask && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Add New Task</h3>
            <button 
              onClick={() => setShowAddTask(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleAddNewTask} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Title
              </label>
              <input
                type="text"
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project
                </label>
                <select
                  value={newTask.projectId}
                  onChange={(e) => setNewTask(prev => ({ ...prev, projectId: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={newTask.status}
                  onChange={(e) => setNewTask(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="review">Review</option>
                  <option value="completed">Done</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
            </div>
                        
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddTask(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              >
                Add Task
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading tasks...</p>
        </div>
      ) : selectedProject === 'all' ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Please select a specific project to view tasks.</p>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">
          <p>{error}</p>
          <button 
            onClick={loadTasks}
            className="text-blue-500 hover:text-blue-600 mt-2"
          >
            Try Again
          </button>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {columns.map(column => (
          <div key={column.id} className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-gray-700 mb-4 flex items-center justify-between">
              <span>{column.title}</span>
              <span className="text-sm bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                {tasks.filter(task => task.status === column.id).length}
              </span>
            </h3>
            <div className="space-y-3">
              {tasks
                .filter(task => task.status === column.id)
                .map((task, index) => (
                  <div 
                    key={`${task.id}-${index}`} 
                    className={`bg-white rounded-lg p-4 shadow-sm border-l-4 ${getStatusColor(task.status)} hover:shadow-md transition-shadow cursor-pointer`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{task.title}</h4>
                      {getStatusIcon(task.status)}
                    </div>
                    {task.description && (
                      <p className="text-gray-600 text-sm mb-3">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {task.project && (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                          {task.project}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-3">
                      {task.dueDate && (
                        <span className="text-xs text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                      {task.assignee && (
                        <div className="flex items-center">
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-700">
                            {task.assignee.split(' ').map(n => n[0]).join('')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}              
              <button 
                onClick={() => {
                  setNewTask(prev => ({
                    ...prev,
                    status: column.id,
                    projectId: selectedProject !== 'all' ? selectedProject : ''
                  }));
                  setShowAddTask(true);
                }}
                className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 flex items-center justify-center"
                disabled={selectedProject === 'all'}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Task
              </button>
            </div>
          </div>
        ))}
      </div>)}
    </div>
  );
}